import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkle, Pulse, Compass } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function InsightsPage() {
  const [patterns, setPatterns] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [weeklyLoading, setWeeklyLoading] = useState(true);
  const [patternsLoading, setPatternsLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get("/insights/weekly")
      .then((r) => alive && setWeekly(r.data))
      .catch(() => {})
      .finally(() => alive && setWeeklyLoading(false));
    api
      .get("/insights/patterns")
      .then((r) => alive && setPatterns(r.data))
      .catch(() => {})
      .finally(() => alive && setPatternsLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="px-5 sm:px-10 md:px-20 py-10 sm:py-14" data-testid="insights-page">
      <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ink-mute">Your clarity</p>
      <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-ink mt-3 mb-10 sm:mb-12 leading-tight">What’s been moving in you.</h1>

      <div className="grid md:grid-cols-12 gap-6">
        {/* Weekly summary headline */}
        {weeklyLoading ? (
          <WeeklySkeleton />
        ) : (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="md:col-span-12 rounded-3xl bg-clay/10 border border-clay/30 p-6 sm:p-10 md:p-14"
            data-testid="weekly-summary"
          >
            <div className="flex items-center gap-2 mb-6">
              <Sparkle size={14} weight="fill" className="text-clay" />
              <span className="text-xs uppercase tracking-[0.3em] text-ink-mute">
                Weekly clarity · {weekly?.week_start} → {weekly?.week_end} · {weekly?.entry_count} entries
              </span>
            </div>
            <h2 className="font-serif-display italic text-3xl md:text-4xl text-ink leading-snug max-w-3xl">
              {weekly?.headline}
            </h2>

            {weekly?.insights?.length > 0 && (
              <div className="mt-10 grid md:grid-cols-2 gap-8">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-ink-mute mb-3">What I noticed</p>
                  <ul className="space-y-3 text-ink-soft leading-relaxed">
                    {weekly.insights.map((i) => (
                      <li key={i} className="flex gap-3">
                        <span className="text-clay font-serif-display text-xl leading-none">·</span>
                        <span>{i}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-ink-mute mb-3">Small invitations</p>
                  <ul className="space-y-3 text-ink leading-relaxed">
                    {weekly.move_forward?.map((m) => (
                      <li key={m} className="flex gap-3 font-serif-display text-lg italic">
                        <span className="text-clay not-italic font-sans text-sm">→</span>
                        <span>{m}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </motion.section>
        )}

        {/* Themes */}
        {patternsLoading ? (
          <ThemesSkeleton />
        ) : (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-7 rounded-3xl bg-canvas-warm border border-line p-6 sm:p-10"
            data-testid="themes-card"
          >
            <div className="flex items-center gap-2 mb-6">
              <Pulse size={18} weight="thin" className="text-ink-soft" />
              <span className="text-xs uppercase tracking-[0.3em] text-ink-mute">Recurring themes</span>
            </div>
            {patterns?.themes?.length > 0 ? (
              <div className="space-y-5">
                {patterns.themes.map((t) => (
                  <div key={t.label} className="border-b border-line pb-5 last:border-0 last:pb-0">
                    <div className="flex items-baseline justify-between gap-4">
                      <h3 className="font-serif-display text-2xl text-ink">{t.label}</h3>
                      <span className="font-mono text-xs text-ink-mute">×{t.count}</span>
                    </div>
                    <p className="text-ink-soft text-sm leading-relaxed mt-1">{t.description}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-ink-mute italic">Themes will surface once you have a few entries.</p>
            )}
            {patterns?.emotional_arc && (
              <p className="mt-8 font-serif-display italic text-lg text-clay-dark">{patterns.emotional_arc}</p>
            )}
          </motion.section>
        )}

        {/* Triggers + choices */}
        {patternsLoading ? (
          <SideSkeleton />
        ) : (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="md:col-span-5 space-y-6"
          >
            <div className="rounded-3xl bg-canvas-warm border border-line p-8" data-testid="triggers-card">
              <div className="flex items-center gap-2 mb-5">
                <Compass size={18} weight="thin" className="text-ink-soft" />
                <span className="text-xs uppercase tracking-[0.3em] text-ink-mute">Triggers</span>
              </div>
              {patterns?.triggers?.length > 0 ? (
                <ul className="space-y-2 text-ink-soft text-sm">
                  {patterns.triggers.map((t) => (
                    <li key={t} className="flex gap-2">
                      <span className="text-clay">·</span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-ink-mute italic text-sm">None yet.</p>
              )}
            </div>

            <div className="rounded-3xl bg-canvas-warm border border-line p-8" data-testid="choices-card">
              <div className="flex items-center gap-2 mb-5">
                <Sparkle size={16} weight="thin" className="text-ink-soft" />
                <span className="text-xs uppercase tracking-[0.3em] text-ink-mute">Recurring choices</span>
              </div>
              {patterns?.recurring_choices?.length > 0 ? (
                <ul className="space-y-2 text-ink-soft text-sm">
                  {patterns.recurring_choices.map((c) => (
                    <li key={c} className="flex gap-2">
                      <span className="text-clay">·</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-ink-mute italic text-sm">None yet.</p>
              )}
            </div>
          </motion.section>
        )}
      </div>
    </div>
  );
}

function ShimmerBar({ className = "" }) {
  return <div className={`bg-canvas-warm rounded animate-pulse ${className}`} />;
}

function WeeklySkeleton() {
  return (
    <div className="md:col-span-12 rounded-3xl bg-clay/10 border border-clay/30 p-6 sm:p-10 md:p-14" data-testid="weekly-skeleton">
      <div className="flex items-center gap-2 mb-6">
        <Sparkle size={14} weight="fill" className="text-clay/40" />
        <ShimmerBar className="h-3 w-72" />
      </div>
      <ShimmerBar className="h-9 w-4/5 mb-3" />
      <ShimmerBar className="h-9 w-3/5 mb-10" />
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-3">
          <ShimmerBar className="h-3 w-32" />
          <ShimmerBar className="h-4 w-full" />
          <ShimmerBar className="h-4 w-11/12" />
          <ShimmerBar className="h-4 w-4/5" />
        </div>
        <div className="space-y-3">
          <ShimmerBar className="h-3 w-32" />
          <ShimmerBar className="h-4 w-full" />
          <ShimmerBar className="h-4 w-10/12" />
        </div>
      </div>
      <p className="mt-8 text-xs text-ink-mute italic tracking-wider">Listening to your week…</p>
    </div>
  );
}

function ThemesSkeleton() {
  return (
    <div className="md:col-span-7 rounded-3xl bg-canvas-warm border border-line p-6 sm:p-10" data-testid="themes-skeleton">
      <div className="flex items-center gap-2 mb-6">
        <Pulse size={18} weight="thin" className="text-ink-mute/50" />
        <ShimmerBar className="h-3 w-40" />
      </div>
      <div className="space-y-5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="border-b border-line pb-5 last:border-0">
            <div className="flex items-baseline justify-between mb-2">
              <ShimmerBar className="h-6 w-48" />
              <ShimmerBar className="h-3 w-8" />
            </div>
            <ShimmerBar className="h-3 w-full" />
            <ShimmerBar className="h-3 w-10/12 mt-2" />
          </div>
        ))}
      </div>
    </div>
  );
}

function SideSkeleton() {
  return (
    <div className="md:col-span-5 space-y-6" data-testid="side-skeleton">
      {[0, 1].map((i) => (
        <div key={i} className="rounded-3xl bg-canvas-warm border border-line p-8">
          <ShimmerBar className="h-3 w-24 mb-5" />
          <div className="space-y-2.5">
            <ShimmerBar className="h-3 w-full" />
            <ShimmerBar className="h-3 w-5/6" />
            <ShimmerBar className="h-3 w-4/5" />
          </div>
        </div>
      ))}
    </div>
  );
}
